import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  create_drum_label,
  regenerate_missing_drum_pdfs,
  get_drum_labels,
  get_drum_label,
  clean_drum,
  allocate_drum_batch,
  change_drum_status,
  get_drum_counts,
  get_drum_logs,
  bulk_clean_drums,
  bulk_allocate_drums,
  bulk_status_change_drums,
  generate_usage_log_report,
  bulkInactivateDrumsApi,
  reactivateDrumApi
} from './api';

const initialState = {
  loading: false,
  list: [],
  listPagination: {},
  pendingCount: 0,
  current: null,
  counts: { to_be_cleaned: 0, cleaned: 0, in_use: 0, inactive: 0 },
  stalePendingCount: 0,
  drumLogs: [],
  drumLogsLoading: false,
  reportGenerating: false
};

const actions = {
  CREATE_DRUM_LABEL: 'drumLabel/CREATE_DRUM_LABEL',
  REGENERATE_MISSING_DRUM_PDFS: 'drumLabel/REGENERATE_MISSING_DRUM_PDFS',
  GET_DRUM_LABELS: 'drumLabel/GET_DRUM_LABELS',
  GET_DRUM_LABEL: 'drumLabel/GET_DRUM_LABEL',
  CLEAN_DRUM: 'drumLabel/CLEAN_DRUM',
  ALLOCATE_DRUM: 'drumLabel/ALLOCATE_DRUM',
  STATUS_CHANGE_DRUM: 'drumLabel/STATUS_CHANGE_DRUM',
  GET_DRUM_COUNTS: 'drumLabel/GET_DRUM_COUNTS',
  GET_DRUM_LOGS: 'drumLabel/GET_DRUM_LOGS',
  BULK_CLEAN: 'drumLabel/BULK_CLEAN',
  BULK_ALLOCATE: 'drumLabel/BULK_ALLOCATE',
  BULK_STATUS_CHANGE: 'drumLabel/BULK_STATUS_CHANGE',
  GENERATE_USAGE_LOG_REPORT: 'drumLabel/GENERATE_USAGE_LOG_REPORT',
  BULK_INACTIVATE: 'drumLabel/BULK_INACTIVATE',
  REACTIVATE_DRUM: 'drumLabel/REACTIVATE_DRUM'
};

export const createDrumLabel = createAsyncThunk(
  actions.CREATE_DRUM_LABEL,
  async (payload) => {
    return await create_drum_label(payload);
  }
);

export const regenerateMissingDrumPdfs = createAsyncThunk(
  actions.REGENERATE_MISSING_DRUM_PDFS,
  async () => {
    return await regenerate_missing_drum_pdfs();
  }
);

export const getDrumLabels = createAsyncThunk(
  actions.GET_DRUM_LABELS,
  async (payload) => {
    return await get_drum_labels(payload);
  }
);

export const getDrumLabel = createAsyncThunk(
  actions.GET_DRUM_LABEL,
  async (id) => {
    return await get_drum_label(id);
  }
);

export const cleanDrum = createAsyncThunk(actions.CLEAN_DRUM, async (id) => {
  return await clean_drum(id);
});

export const allocateDrum = createAsyncThunk(
  actions.ALLOCATE_DRUM,
  async ({ id, values }) => {
    return await allocate_drum_batch(id, values);
  }
);

export const statusChangeDrum = createAsyncThunk(
  actions.STATUS_CHANGE_DRUM,
  async (id) => {
    return await change_drum_status(id);
  }
);

export const getDrumCounts = createAsyncThunk(
  actions.GET_DRUM_COUNTS,
  async () => {
    return await get_drum_counts();
  }
);

export const getDrumLogs = createAsyncThunk(
  actions.GET_DRUM_LOGS,
  async (drumLabelNumber) => {
    return await get_drum_logs(drumLabelNumber);
  }
);

export const bulkCleanDrums = createAsyncThunk(
  actions.BULK_CLEAN,
  async (payload) => {
    return await bulk_clean_drums(payload);
  }
);

export const bulkAllocateDrums = createAsyncThunk(
  actions.BULK_ALLOCATE,
  async (payload) => {
    return await bulk_allocate_drums(payload);
  }
);

export const bulkStatusChangeDrums = createAsyncThunk(
  actions.BULK_STATUS_CHANGE,
  async (payload) => {
    return await bulk_status_change_drums(payload);
  }
);

export const generateUsageLogReport = createAsyncThunk(
  actions.GENERATE_USAGE_LOG_REPORT,
  async (payload) => {
    return await generate_usage_log_report(payload);
  }
);

export const bulkInactivateDrums = createAsyncThunk(
  actions.BULK_INACTIVATE,
  async (payload, { rejectWithValue }) => {
    try {
      return await bulkInactivateDrumsApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const reactivateDrum = createAsyncThunk(
  actions.REACTIVATE_DRUM,
  async (payload, { rejectWithValue }) => {
    try {
      return await reactivateDrumApi(payload);
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

const drumLabelSlice = createSlice({
  name: 'drumLabel',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createDrumLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDrumLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(createDrumLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(regenerateMissingDrumPdfs.pending, (state) => {
        state.loading = true;
      })
      .addCase(regenerateMissingDrumPdfs.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) {
          message.success(msg);
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(regenerateMissingDrumPdfs.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getDrumLabels.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDrumLabels.fulfilled, (state, action) => {
        state.loading = false;
        const {
          success,
          data,
          pagination,
          pending_count,
          stale_pending_count,
          message: msg
        } = action.payload || {};
        if (success) {
          state.list = data || [];
          state.listPagination = pagination || {};
          state.pendingCount = pending_count || 0;
          state.stalePendingCount = stale_pending_count || 0;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getDrumLabels.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getDrumLabel.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDrumLabel.fulfilled, (state, action) => {
        state.loading = false;
        const { success, data, message: msg } = action.payload || {};
        if (success) {
          state.current = data;
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(getDrumLabel.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(cleanDrum.pending, (state) => {
        state.loading = true;
      })
      .addCase(cleanDrum.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(cleanDrum.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(allocateDrum.pending, (state) => {
        state.loading = true;
      })
      .addCase(allocateDrum.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(allocateDrum.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(statusChangeDrum.pending, (state) => {
        state.loading = true;
      })
      .addCase(statusChangeDrum.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(statusChangeDrum.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(getDrumCounts.fulfilled, (state, action) => {
        const { success, data } = action.payload || {};
        if (success && data) state.counts = data;
      })
      .addCase(getDrumCounts.rejected, () => {});

    builder
      .addCase(getDrumLogs.pending, (state) => {
        state.drumLogsLoading = true;
        state.drumLogs = [];
      })
      .addCase(getDrumLogs.fulfilled, (state, action) => {
        state.drumLogsLoading = false;
        const { success, data } = action.payload || {};
        if (success) state.drumLogs = data || [];
      })
      .addCase(getDrumLogs.rejected, (state) => {
        state.drumLogsLoading = false;
      });

    builder
      .addCase(bulkCleanDrums.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkCleanDrums.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkCleanDrums.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkAllocateDrums.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkAllocateDrums.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkAllocateDrums.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkStatusChangeDrums.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkStatusChangeDrums.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkStatusChangeDrums.rejected, (state, action) => {
        state.loading = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(generateUsageLogReport.pending, (state) => {
        state.reportGenerating = true;
      })
      .addCase(generateUsageLogReport.fulfilled, (state, action) => {
        state.reportGenerating = false;
        const { success, data, message: msg } = action.payload || {};
        if (success && data) {
          const filename = String(data).split(/[?#]/)[0].split('/').pop();
          window.open(
            `${process.env.REACT_APP_STORAGE_URL}${filename}`,
            '_blank'
          );
          message.success('Report generated successfully.');
        } else if (msg) {
          message.error(msg);
        }
      })
      .addCase(generateUsageLogReport.rejected, (state, action) => {
        state.reportGenerating = false;
        message.error(action.error?.message);
      });

    builder
      .addCase(bulkInactivateDrums.pending, (state) => {
        state.loading = true;
      })
      .addCase(bulkInactivateDrums.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(bulkInactivateDrums.rejected, (state, action) => {
        state.loading = false;
        const msg = action.payload?.message || action.error?.message;
        if (msg) message.error(msg);
      });

    builder
      .addCase(reactivateDrum.pending, (state) => {
        state.loading = true;
      })
      .addCase(reactivateDrum.fulfilled, (state, action) => {
        state.loading = false;
        const { success, message: msg } = action.payload || {};
        if (success) message.success(msg);
        else if (msg) message.error(msg);
      })
      .addCase(reactivateDrum.rejected, (state, action) => {
        state.loading = false;
        const msg = action.payload?.message || action.error?.message;
        if (msg) message.error(msg);
      });
  }
});

export default drumLabelSlice.reducer;
